/*
 * Thin wrapper around a federated dynamic import that centralizes failure
 * reporting. On success it resolves to exactly what the import resolves to; on
 * failure it reports the error (classified + tagged via noticeImportError) and
 * re-throws so each call site keeps its own fallback / side-effect handling.
 *
 *   importFederation('inventory/featureFlagSlugs')
 *     .then((m) => m.default)
 *     .catch(() => ({}));   // site-specific fallback still lives at the call site
 *
 * Why the federation runtime instead of `import(specifier)`:
 * vite-plugin-federation only rewrites *string-literal* `import('remote/x')` at
 * build time; a variable specifier can't be analyzed, so it would never be
 * transformed and would fail to resolve at runtime. Instead we resolve through
 * the runtime API (`virtual:__federation__`): split the specifier into the
 * remote name + exposed module and call getRemote(name, './module'), then
 * wrapDefault — exactly what the plugin's literal-import transform emits.
 *
 * Note: retry loops (e.g. TransportationHub) should NOT use this — it reports on
 * every failure, so a 3x retry would emit 3 reports. Those sites call
 * noticeImportError once at their final-failure handler instead.
 */

import noticeImportError from '@/utilities/mfe/noticeImportError';

/**
 * @param {string} specifier - the import specifier, e.g. 'inventory/featureFlagSlugs'
 * @returns {Promise<unknown>} the imported module (identical to a literal import() on success)
 */
const importFederation = async (specifier) => {
  try {
    // 'inventory/featureFlagSlugs' -> remote 'inventory', exposed './featureFlagSlugs'
    const slash = specifier.indexOf('/');
    const remoteName = specifier.slice(0, slash);
    const exposedModule = `.${specifier.slice(slash)}`;

    const {
      __federation_method_getRemote: getRemote,
      __federation_method_wrapDefault: wrapDefault,
      // eslint-disable-next-line import/no-unresolved
    } = await import('virtual:__federation__');

    const module = await getRemote(remoteName, exposedModule);
    // `true` mirrors the plugin's needWrap for `from: 'vite'` remotes, so callers
    // can read `.default` just like they did with a literal import().
    return wrapDefault(module, true);
  } catch (error) {
    noticeImportError(specifier, error);
    throw error;
  }
};

export default importFederation;
